import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Onboarding completion is determined entirely server-side: the user has
 * finished setup iff there is an `athlete_profile` row AND an active `context`
 * row (effective_to is null) for their user_id.
 *
 * This replaces the BW3 path that only queried `athlete_profile` — Jonas's
 * BW7 phone session reproduced the redirect loop because both rows existed
 * but the gate was missing a re-fetch when sessions changed across devices.
 *
 * Returns:
 *   - status: "loading" | "ready" | "needs_onboarding" | "error"
 *   - hasProfile / hasContext booleans for callers that need finer grain
 *     (e.g. /onboarding pre-filling existing data).
 *   - error surfaces the underlying Supabase error so the caller can decide
 *     whether to bounce to /onboarding (rare) or surface a real error.
 */

export type OnboardingStatus =
  | { status: "loading" }
  | { status: "ready"; hasProfile: true; hasContext: true }
  | {
      status: "needs_onboarding";
      hasProfile: boolean;
      hasContext: boolean;
    }
  | { status: "error"; error: unknown };

export function useOnboardingStatus(): OnboardingStatus {
  const { session, loading: sessionLoading } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["onboarding_status", userId],
    enabled: !sessionLoading && Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return { hasProfile: false, hasContext: false };
      const [profileRes, contextRes] = await Promise.all([
        supabase
          .from("athlete_profile")
          .select("id")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase
          .from("context")
          .select("id")
          .eq("user_id", userId)
          .is("effective_to", null)
          .order("effective_from", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (profileRes.error) throw profileRes.error;
      if (contextRes.error) throw contextRes.error;
      return {
        hasProfile: Boolean(profileRes.data),
        hasContext: Boolean(contextRes.data),
      };
    },
  });

  if (sessionLoading || (Boolean(userId) && q.isLoading)) {
    return { status: "loading" };
  }
  if (q.isError) return { status: "error", error: q.error };
  if (!q.data) return { status: "loading" };
  if (q.data.hasProfile && q.data.hasContext) {
    return { status: "ready", hasProfile: true, hasContext: true };
  }
  return {
    status: "needs_onboarding",
    hasProfile: q.data.hasProfile,
    hasContext: q.data.hasContext,
  };
}
